import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChatSummary, Message, MessagePairResponse } from '../domain';
import { buildAuthHeaders } from '../services/apiClient';
import * as chatService from '../services/chatService';
import { extractErrorMessage } from '../utils/errors';

type UseChatWorkspaceParams = {
  token: string | null;
  guestId: string;
};

type UseChatWorkspaceResult = {
  chats: ChatSummary[];
  messages: Message[];
  selectedChatId: number | null;
  activeChat: ChatSummary | null;
  activeAgentId: string;
  loading: boolean;
  loadingMessages: boolean;
  initializing: boolean;
  error: string | null;
  handleSelectChat: (chatId: number) => Promise<void>;
  handleSendMessage: (content: string) => Promise<MessagePairResponse | null>;
  handleNewChat: () => void;
  handlePersonaSelection: (id: string) => void;
  resetWorkspace: () => void;
  forceLogout: boolean;
  acknowledgeForceLogout: () => void;
};

const DEFAULT_AGENT = 'python_expert';

export function useChatWorkspace({ token, guestId }: UseChatWorkspaceParams): UseChatWorkspaceResult {
  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [selectedChatId, setSelectedChatId] = useState<number | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [activeAgentId, setActiveAgentId] = useState<string>(DEFAULT_AGENT);
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forceLogout, setForceLogout] = useState(false);

  const authHeaders = useMemo(() => buildAuthHeaders(token, guestId), [token, guestId]);

  const activeChat = useMemo(() => {
    const list = Array.isArray(chats) ? chats : [];
    return list.find((chat) => chat.id === selectedChatId) ?? null;
  }, [chats, selectedChatId]);

  const resetWorkspace = useCallback(() => {
    setChats([]);
    setSelectedChatId(null);
    setMessages([]);
    setActiveAgentId(DEFAULT_AGENT);
    setError(null);
  }, []);

  const acknowledgeForceLogout = useCallback(() => {
    setForceLogout(false);
  }, []);

  const fetchChats = useCallback(async () => {
    const data = await chatService.fetchChats(authHeaders);
    setChats(data);
  }, [authHeaders]);

  useEffect(() => {
    let cancelled = false;
    async function initialize() {
      try {
        setInitializing(true);
        await fetchChats();
      } catch (error) {
        console.error('Initialization failed', error);
        if (!cancelled && token) {
          setForceLogout(true);
        }
      } finally {
        if (!cancelled) {
          setInitializing(false);
        }
      }
    }
    initialize();
    return () => {
      cancelled = true;
    };
  }, [fetchChats, token]);

  useEffect(() => {
    if (activeChat) {
      setActiveAgentId(activeChat.agent_id);
    }
  }, [activeChat]);

  const ensureActiveChat = useCallback(async () => {
    if (selectedChatId !== null) {
      return selectedChatId;
    }
    const newChat = await chatService.createChat(activeAgentId, authHeaders);
    setChats((prev) => [newChat, ...prev]);
    setSelectedChatId(newChat.id);
    return newChat.id;
  }, [activeAgentId, authHeaders, selectedChatId]);

  const refreshChatsAfterActivity = useCallback(async () => {
    try {
      await fetchChats();
    } catch (error) {
      console.error('Failed to refresh chats', error);
    }
  }, [fetchChats]);

  const handleSendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || loading) {
        return null;
      }
      setLoading(true);
      setError(null);
      try {
        const chatId = await ensureActiveChat();
        const response = await chatService.sendMessage(chatId, content.trim(), authHeaders);
        setMessages((prev) => [...prev, response.user_message, response.assistant_message]);
        await refreshChatsAfterActivity();
        return response;
      } catch (error) {
        const message = extractErrorMessage(error);
        if (message === 'Invalid token' || message === 'User not found') {
          setForceLogout(true);
        } else {
          setError(message);
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [authHeaders, ensureActiveChat, loading, refreshChatsAfterActivity]
  );

  const handleSelectChat = useCallback(
    async (chatId: number) => {
      setLoadingMessages(true);
      setError(null);
      try {
        const detail = await chatService.fetchChatDetail(chatId, authHeaders);
        setSelectedChatId(chatId);
        setMessages(detail.messages);
        setActiveAgentId(detail.agent_id);
      } catch (error) {
        const message = extractErrorMessage(error);
        if (message === 'Invalid token' || message === 'User not found') {
          setForceLogout(true);
        } else {
          setError(message);
        }
      } finally {
        setLoadingMessages(false);
      }
    },
    [authHeaders]
  );

  const handleNewChat = useCallback(() => {
    setSelectedChatId(null);
    setMessages([]);
    setError(null);
  }, []);

  const handlePersonaSelection = useCallback(
    (id: string) => {
      setActiveAgentId((current) => {
        if (current === id) {
          return current;
        }
        if (selectedChatId !== null || messages.length > 0) {
          handleNewChat();
        }
        return id;
      });
    },
    [handleNewChat, messages, selectedChatId]
  );

  return {
    chats,
    messages,
    selectedChatId,
    activeChat,
    activeAgentId,
    loading,
    loadingMessages,
    initializing,
    error,
    handleSelectChat,
    handleSendMessage,
    handleNewChat,
    handlePersonaSelection,
    resetWorkspace,
    forceLogout,
    acknowledgeForceLogout,
  };
}
